import { useCallback, useState, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { assemblyTemplate, selectedTemplateId } from '../state';
import { removeTypename, useSnackBarMessage } from '../utils';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { FullWidthPanel } from './styles';
import {
  AllTemplatesForBuilderDocument,
  EquipmentDetailOptionField,
  EquipmentDetailTypeEnum,
  GetVersionsDocument,
  OptionFieldTypeEnum,
  TemplateTypeEnum,
  useUpdateTemplateMutation,
} from '@/__generated__/graphql';
import { EquipmentDetailType } from '../types';
import { FieldPanel } from '../TemplateForm/styles';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';

type EquipmentDetailsFormProps = {
  structureId: string;
};

export const EquipmentDetailsForm = ({ structureId }: EquipmentDetailsFormProps) => {
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');

  const selectedAssemblyTemplateId = useAtomValue(selectedTemplateId);
  const selectedAssemblyTemplate = useAtomValue(assemblyTemplate);

  const equipmentDetails = useMemo(() => {
    return selectedAssemblyTemplate?.equipmentDetails ?? [];
  }, [selectedAssemblyTemplate]);

  const [localEquipmentDetails, setLocalEquipmentDetails] =
    useState<EquipmentDetailType>(equipmentDetails);

  const [newOptionDrafts, setNewOptionDrafts] = useState<
    Record<string, EquipmentDetailOptionField>
  >({});

  const allFields =
    selectedAssemblyTemplate?.fields?.map((field) => ({
      key: field.key,
      name: field.name,
    })) ?? [];

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  useEffect(() => {
    setLocalEquipmentDetails(equipmentDetails);
    setNewOptionDrafts({});
  }, [equipmentDetails]);

  const handleEdit = useCallback(() => {
    setMode('Edit');
  }, []);

  const handleCancel = useCallback(() => {
    setMode('ReadOnly');
    setLocalEquipmentDetails(equipmentDetails);
    setNewOptionDrafts({});
  }, [equipmentDetails, setLocalEquipmentDetails]);

  const [updateEquipmentDetails] = useUpdateTemplateMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
    onCompleted: (response) => {
      setMode('ReadOnly');
      if (!response?.updateTemplate) {
        showSnackBar('An error occurred while updating equipment details', 'error');
        return;
      }
      showSnackBar('Equipment details saved successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const handleSaveEquipmentDetails = useCallback(async () => {
    if (!selectedAssemblyTemplateId) return;

    const cleanedEquipmentDetails = removeTypename<EquipmentDetailType>(localEquipmentDetails);

    await updateEquipmentDetails({
      variables: {
        input: {
          type: TemplateTypeEnum.Assembly,
          structureId,
          templateId: selectedAssemblyTemplateId,
          equipmentDetails: cleanedEquipmentDetails,
          lastModifiedFor: 'Update Equipment Details',
        },
      },
    });
  }, [localEquipmentDetails, structureId, selectedAssemblyTemplateId, updateEquipmentDetails]);

  const moveOption = (fieldId: string, fromIndex: number, toIndex: number) => {
    if (toIndex < 0) return;

    setLocalEquipmentDetails((prev) =>
      prev.map((equipmentDetails) => {
        if (equipmentDetails.id !== fieldId) return equipmentDetails;

        const existingOptions = equipmentDetails.options ?? [];
        if (toIndex >= existingOptions.length) return equipmentDetails;

        const options = existingOptions.slice();
        const removed = options[fromIndex];
        options.splice(fromIndex, 1);
        options.splice(toIndex, 0, removed);

        return { ...equipmentDetails, options };
      })
    );
  };

  const handleOptionNameChange = (fieldId: string, optionIndex: number, newName: string) => {
    setLocalEquipmentDetails((prev) =>
      prev.map((equipmentDetails) => {
        if (equipmentDetails.id !== fieldId) return equipmentDetails;
        const existingOptions = equipmentDetails.options ?? [];
        const options = existingOptions.slice();
        options[optionIndex] = { ...options[optionIndex], name: newName };
        return { ...equipmentDetails, options };
      })
    );
  };

  const handleNewOptionFieldChange = (fieldId: string, fieldKey: string) => {
    const selectedField = allFields.find((f) => f.key === fieldKey);

    setNewOptionDrafts((prev) => {
      const previous = prev[fieldId] ?? { fieldKey: null, name: '', type: null };
      return {
        ...prev,
        [fieldId]: {
          id: '',
          fieldKey,
          name: selectedField ? selectedField.name : previous.name,
          type: previous.type,
        },
      };
    });
  };

  const handleNewOptionNameChange = (fieldId: string, name: string) => {
    setNewOptionDrafts((prev) => {
      const previous = prev[fieldId] ?? { fieldKey: null, name: '', type: null };
      return {
        ...prev,
        [fieldId]: { ...previous, name },
      };
    });
  };

  const handleNewOptionTypeChange = (fieldId: string, type: OptionFieldTypeEnum) => {
    setNewOptionDrafts((prev) => {
      const previous = prev[fieldId] ?? { fieldKey: null, name: '', type: null };
      return {
        ...prev,
        [fieldId]: { ...previous, type },
      };
    });
  };

  const handleAddNewOptionConfirm = (fieldId: string) => {
    const draft = newOptionDrafts[fieldId];
    if (!draft || !draft.fieldKey || !draft.type) return;

    const selectedField = allFields.find((f) => f.key === draft.fieldKey);
    if (!selectedField) return;

    setLocalEquipmentDetails((prev) =>
      prev.map((equipmentDetails) => {
        if (equipmentDetails.id !== fieldId) return equipmentDetails;

        const newOption = {
          id: '',
          name: draft.name || selectedField.name,
          fieldKey: draft.fieldKey,
          type: draft.type ?? OptionFieldTypeEnum.SimpleText,
        };

        const existingOptions = removeTypename(equipmentDetails.options) ?? [];
        const options = existingOptions.concat(newOption);

        return { ...equipmentDetails, options };
      })
    );

    setNewOptionDrafts((prev) => {
      const copy: Record<string, EquipmentDetailOptionField> = {};
      Object.keys(prev).forEach((key) => {
        if (key !== fieldId) {
          copy[key] = prev[key];
        }
      });
      return copy;
    });
  };

  const handleDeleteOption = useCallback(
    (fieldId: string, optionIndex: number) => {
      setLocalEquipmentDetails((prev) =>
        prev.map((equipmentDetails) => {
          if (equipmentDetails.id !== fieldId) return equipmentDetails;

          const existingOptions = removeTypename(equipmentDetails.options) ?? [];
          const options = existingOptions.filter((_, index) => index !== optionIndex);

          return { ...equipmentDetails, options };
        })
      );
    },
    [setLocalEquipmentDetails]
  );

  return (
    <>
      {localEquipmentDetails.length === 0 ? (
        <Box sx={{ pt: 2 }}>
          <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
            <EmptyStatePlaceholder type="template" message="No Equipment Details Found" />
          </FieldPanel>
        </Box>
      ) : (
        <>
          <Stack
            width="100%"
            marginTop="1.2rem"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            height="4.8rem"
          >
            <Typography fontSize="2.2rem" fontWeight={500}>
              Equipment Details
            </Typography>
            {mode === 'Edit' ? (
              <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveEquipmentDetails}
                  sx={containedButtonStyle}
                >
                  Save
                </Button>
                <Button variant="contained" onClick={handleCancel} sx={containedButtonStyle}>
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
                <Tooltip title="Edit Equipment Details" placement="top" arrow>
                  <IconButton size="medium" onClick={handleEdit}>
                    <Edit
                      fontSize="inherit"
                      sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>

          <FullWidthPanel>
            <Stack sx={{ flex: 1, py: '1.6rem', gap: '1.6rem' }}>
              {localEquipmentDetails.map((field) => {
                const isTemplateList = field.type === EquipmentDetailTypeEnum.TemplateList;

                if (!isTemplateList) {
                  return (
                    <Stack
                      key={field.id}
                      spacing={0.5}
                      sx={{
                        border: `1px solid ${abyssColors.neutral[300]}`,
                        borderRadius: 1,
                        p: 2,
                      }}
                    >
                      <Typography fontWeight={600}>{field.name}</Typography>
                      <Typography variant="caption" color={abyssColors.primary[300]}>
                        Type: {field.type}
                      </Typography>
                      {field.options && field.options.length > 0 ? (
                        <Typography variant="body2" color={abyssColors.primary[300]}>
                          Options: {field.options.map((option) => option.name).join(', ')}
                        </Typography>
                      ) : null}
                    </Stack>
                  );
                }

                const draft = newOptionDrafts[field.id] ?? {
                  fieldKey: null,
                  name: '',
                  type: OptionFieldTypeEnum.SimpleText,
                };

                return (
                  <Stack
                    key={field.id}
                    spacing={1}
                    sx={{ border: `1px solid ${abyssColors.neutral[300]}`, borderRadius: 1, p: 2 }}
                  >
                    <Typography fontWeight={600}>
                      {field.name || 'Default Template List'}
                    </Typography>

                    {(field.options ?? []).map((option, index) => (
                      <Stack
                        key={option.id || field.id + '-' + index}
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{ width: '100%' }}
                      >
                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => moveOption(field.id, index, index - 1)}
                            disabled={index === 0 || mode === 'ReadOnly'}
                          >
                            <ArrowUpward fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveOption(field.id, index, index + 1)}
                            disabled={
                              index === (field.options?.length ?? 1) - 1 || mode === 'ReadOnly'
                            }
                          >
                            <ArrowDownward fontSize="inherit" />
                          </IconButton>
                        </Stack>

                        <TextField
                          size="small"
                          label="Display Name"
                          value={option.name}
                          onChange={(e) =>
                            mode === 'Edit' &&
                            handleOptionNameChange(field.id, index, e.target.value)
                          }
                          disabled={mode === 'ReadOnly'}
                          sx={{ width: 320, flexShrink: 0 }}
                        />

                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{
                            minWidth: 0,
                            flexGrow: 1,
                            flexShrink: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <Typography variant="caption" color={abyssColors.primary[300]}>
                            key: {option.fieldKey}
                          </Typography>
                          <Typography variant="caption" color={abyssColors.primary[300]}>
                            type: {option.type}
                          </Typography>
                        </Stack>

                        <IconButton
                          size="small"
                          onClick={() => handleDeleteOption(field.id, index)}
                          disabled={mode === 'ReadOnly'}
                          sx={{ ml: 'auto', flexShrink: 0 }}
                        >
                          <Delete
                            fontSize="inherit"
                            sx={{
                              cursor: 'pointer',
                              color:
                                mode === 'ReadOnly'
                                  ? abyssColors.primary[300]
                                  : abyssColors.error[700],
                            }}
                          />
                        </IconButton>
                      </Stack>
                    ))}

                    {mode === 'Edit' && (
                      <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                        <Autocomplete
                          sx={{ minWidth: 220 }}
                          size="small"
                          options={allFields}
                          getOptionLabel={(o) => o.name}
                          value={
                            draft.fieldKey
                              ? allFields.find((f) => f.key === draft.fieldKey) || null
                              : null
                          }
                          onChange={(_, value) => {
                            if (value) {
                              handleNewOptionFieldChange(field.id, value.key ?? '');
                            }
                          }}
                          renderInput={(params) => <TextField {...params} label="Select field" />}
                        />

                        <TextField
                          size="small"
                          label="Display Name"
                          value={draft.name}
                          onChange={(e) => handleNewOptionNameChange(field.id, e.target.value)}
                        />

                        <TextField
                          select
                          size="small"
                          label="Display Type"
                          value={draft.type || ''}
                          sx={{ minWidth: 160 }}
                        >
                          {Object.values(OptionFieldTypeEnum).map((t) => (
                            <MenuItem
                              key={t}
                              value={t}
                              onClick={() => handleNewOptionTypeChange(field.id, t)}
                            >
                              {t}
                            </MenuItem>
                          ))}
                        </TextField>

                        <Button
                          variant="outlined"
                          onClick={() => handleAddNewOptionConfirm(field.id)}
                          disabled={!draft.fieldKey || !draft.type}
                          startIcon={<AddCircleIcon />}
                        >
                          Add option
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </FullWidthPanel>
        </>
      )}
    </>
  );
};
